import { AgeGenderDialogStep } from '@/components/age-and-gender/dialog';
import useAgeGenderDialog from '@/hooks/use-age-gender-dialog';
import { SkipDeleteRedirectQueryKey } from '@rhg/components/delete-account/types';
import useAuthRedirect from '@rhg/hooks/use-auth-redirect';
import { usePreviousRegistrationFlow } from '@rhg/hooks/use-previous-registration-flow';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import {
    RegistrationDialogOpenQueryKey,
    useRegistrationDialog,
} from '@rhg/hooks/use-registration-dialog';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { UserBlocker } from '@rhg/hooks/use-user-blocker/types';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import { Box } from '@rhg/libs/material';
import { ConciergeSource } from '@rhg/utils/constants';
import { routePattern, routes } from '@rhg/utils/routes';
import { getIsInitialSSO } from '@rhg/utils/sso';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { SkipAutoSubmitMagicLinkQueryKey } from '../registration/auto-submit-magic-link-step/types';
import { SkipLinkingSSOQueryKey } from '../registration/linking-sso-step/types';
import { FullWidthLinearProgress } from '../ui/progress';

const styleHidden = { display: 'none' };

const logRedirectError = (err: unknown) =>
    logger.error('Failed to redirect in user blocker gate', {}, err as Error);

export const UserBlockerGate = ({ children }: PropsWithChildren) => {
    const {
        isLoading: isPreviousRegistrationFlowLoading,
        previousRegistrationFlow,
    } = usePreviousRegistrationFlow();
    const { isRegistrationDialogOpen } = useRegistrationDialog();
    const { pathname, query, replace, isReady: routerIsReady } = useRouter();
    const { isLoading: isAuthLoading, isAuthenticated } = useAuth();
    const { redirect } = useAuthRedirect();
    const { userBlocker, isLoading: isUserBlockerLoading } = useUserBlocker();
    const isSSO = getIsInitialSSO();
    const [hasFinished, setHasFinished] = useState(false);
    const {
        setOpenStep: setAgeGenderDialogOpenStep,
        isOpen: isAgeGenderDialogOpen,
    } = useAgeGenderDialog();

    // This is to check the requirements for whether the onboarding check should fire or not
    const isLoaded = useMemo(() => {
        if (isAuthLoading || !routerIsReady) {
            return false;
        }

        if (
            !isAuthenticated ||
            (!isUserBlockerLoading && !isPreviousRegistrationFlowLoading)
        ) {
            return true;
        }

        return false;
    }, [
        isAuthLoading,
        isAuthenticated,
        isPreviousRegistrationFlowLoading,
        isUserBlockerLoading,
        routerIsReady,
    ]);

    const isOnExcludedPage = useMemo(() => {
        if (pathname === routes.auth) {
            return true;
        }
        if (pathname.includes('/components')) {
            return true;
        }

        if (pathname === routes.dashboard && !isAuthenticated) {
            return true;
        }

        return [
            routes.error,
            routes.expired,
            routes.notFound,
            routes.login,
            routes.reconsent,
            routes.standaloneResults,
            routes.standaloneScreening,
            routes.nonUsResident,
            routes.signup,
            routes.saml,
            routes.sso,
            routes.ssoInfo,
            routes.registration,
            routes.programDetails(),
            routes.partnerMemberActivation,
            routePattern.partnerMemberActivationToken,
        ].includes(pathname);
    }, [isAuthenticated, pathname]);

    const needsAuthRedirect = useMemo(
        () => isLoaded && pathname === routes.auth,
        [isLoaded, pathname]
    );

    const needsUserBlockerHandling = useMemo(
        () =>
            isLoaded &&
            !query[SkipDeleteRedirectQueryKey] &&
            !isOnExcludedPage &&
            !!userBlocker &&
            [
                ...(query[SkipLinkingSSOQueryKey]
                    ? []
                    : [UserBlocker.LinkingSSO]),
                ...(query[SkipAutoSubmitMagicLinkQueryKey]
                    ? []
                    : [UserBlocker.AutoSubmitMagicLink]),
                UserBlocker.Expired,
                UserBlocker.CountryUnsponsored,
                UserBlocker.CountryUnsponsoredHasAccessCode,
                UserBlocker.Tos,
                UserBlocker.CCHDUnsponsored,
                UserBlocker.ReceivingInvitation,
                // These are post-sponsorship blockers, if we are already in the registration dialog flow, they should be ignored.
                // If the user exits the flow they'll show
                ...(isRegistrationDialogOpen
                    ? []
                    : [
                          UserBlocker.Reconsent,
                          UserBlocker.CountrySponsored,
                          UserBlocker.CCHDSponsored,
                          UserBlocker.RegistrationAgeGender,
                          UserBlocker.UK,
                          UserBlocker.China,
                          UserBlocker.EU,
                          UserBlocker.India,
                          UserBlocker.Australia,
                          UserBlocker.International,
                          UserBlocker.CommunicationsPreference,
                          UserBlocker.RegistrationParentalConsent,
                          UserBlocker.Onboarding,
                          UserBlocker.ConciergeOnboarding,
                      ]),
                ...(isRegistrationDialogOpen || isAgeGenderDialogOpen
                    ? []
                    : [UserBlocker.StandaloneParentalConsent]),
            ].includes(userBlocker),
        [
            isAgeGenderDialogOpen,
            isLoaded,
            isOnExcludedPage,
            isRegistrationDialogOpen,
            query,
            userBlocker,
        ]
    );

    const countryConsentUserBlockerCurrentStep = useMemo(() => {
        switch (userBlocker) {
            case UserBlocker.UK:
                return RegistrationFlowStep.UkCountryConsent;
            case UserBlocker.India:
                return RegistrationFlowStep.InCountryConsent;
            case UserBlocker.China:
                return RegistrationFlowStep.CnCountryConsent;
            case UserBlocker.EU:
                return RegistrationFlowStep.EuCountryConsent;
            case UserBlocker.Australia:
                return RegistrationFlowStep.AuCountryConsent;
            case UserBlocker.International:
                return RegistrationFlowStep.InternationalCountryConsent;
            default:
                return null;
        }
    }, [userBlocker]);

    /**
     * This effect makes sure that users are go through the onboarding flow correctly.
     * For now the FTUE flow is largely unmanaged by BE state, this will be done later.
     * Instead the user will be able to move around freely instead of being forced into the flow
     */
    useEffect(() => {
        if (!isLoaded) {
            return;
        }

        /**
         * If the user lands on the /auth page and has a redirect, send them to it.
         */
        if (needsAuthRedirect) {
            if (isSSO && !userBlocker) {
                /*
                 ** If the user is coming in through SSO and has no registration step assigned, send them to the dashboard
                 ** This can occur when the SSO user is already sponsored by a related parent or child partner
                 */
                replace(routes.dashboard).catch(logRedirectError);
            } else if (redirect) {
                replace(redirect).catch(logRedirectError);
            } else {
                replace(routes.dashboard, { query }).catch(logRedirectError);
            }
            setHasFinished(true);
            return;
        }

        /**
         * If the user does not have a first name, or has not accepted TOS, start registration
         */
        if (needsUserBlockerHandling) {
            switch (userBlocker) {
                case UserBlocker.LinkingSSO:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.LinkingSSO,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.AutoSubmitMagicLink:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep:
                                RegistrationFlowStep.AutoSubmitMagicLink,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.ReceivingInvitation:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            registrationFlow:
                                RegistrationFlow.NewInviteeRegistration,
                            currentStep:
                                RegistrationFlowStep.ReceivingInvitation,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.Tos:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.CreateCircle,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.Expired:
                    replace({
                        pathname: routes.expired,
                        // We want to remove rdo from the query since it's handled separately
                        query: omit(query, [RegistrationDialogOpenQueryKey]),
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.CountryUnsponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.AccessCode,
                            registrationFlow: RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.CountryUnsponsoredHasAccessCode:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.Eligibility,
                            registrationFlow: RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.CountrySponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.SelectCountry,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.CommunicationsPreference:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep:
                                RegistrationFlowStep.EmailCommunicationPreferences,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.CCHDUnsponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.CCHDConsent,
                            registrationFlow: RegistrationFlow.SkipSponsorship,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.CCHDSponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.CCHDConsent,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.RegistrationAgeGender:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.AgeGender,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.UK:
                case UserBlocker.India:
                case UserBlocker.China:
                case UserBlocker.EU:
                case UserBlocker.Australia:
                case UserBlocker.International:
                    if (!countryConsentUserBlockerCurrentStep) {
                        // This should never happen but TS needs the check
                        console.error(
                            'No country consent step found for blocker',
                            userBlocker
                        );
                        setHasFinished(true);
                        return;
                    }
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: countryConsentUserBlockerCurrentStep,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.RegistrationParentalConsent:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.ParentalConsent,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.Reconsent:
                    replace({
                        pathname: routes.reconsent,
                        query: {
                            ...query,
                        },
                    }).catch(logRedirectError);
                    break;
                case UserBlocker.StandaloneParentalConsent:
                    setAgeGenderDialogOpenStep(
                        AgeGenderDialogStep.ParentalConsent
                    );
                    break;
                case UserBlocker.ConciergeOnboarding:
                    /* If the user has active concierge FTUE workflow instance, send them back to concierge page */
                    replace(
                        routes.concierge({
                            source: ConciergeSource.Onboarding,
                        })
                    ).catch(logRedirectError);
                    break;
                case UserBlocker.Onboarding:
                    /* If the user has active FTUE workflow instance, send them back to onboarding page */
                    replace(routes.onboarding({})).catch(logRedirectError);
                    break;
            }

            setHasFinished(true);
            return;
        }

        setHasFinished(true);
    }, [
        countryConsentUserBlockerCurrentStep,
        isLoaded,
        isOnExcludedPage,
        isSSO,
        needsAuthRedirect,
        needsUserBlockerHandling,
        pathname,
        previousRegistrationFlow,
        query,
        redirect,
        replace,
        setAgeGenderDialogOpenStep,
        userBlocker,
    ]);

    const isReady =
        isLoaded &&
        hasFinished &&
        !needsAuthRedirect &&
        !needsUserBlockerHandling;

    const shouldShowBecauseLinkingSSO =
        userBlocker === UserBlocker.LinkingSSO && query[SkipLinkingSSOQueryKey];

    const shouldShowBecauseAutoSubmitMagicLink =
        userBlocker === UserBlocker.AutoSubmitMagicLink &&
        query[SkipAutoSubmitMagicLinkQueryKey];

    const shouldShowBecauseUnsponsored =
        userBlocker === UserBlocker.Unsponsored;

    const shouldShowContent =
        isOnExcludedPage ||
        ((!userBlocker ||
            shouldShowBecauseLinkingSSO ||
            shouldShowBecauseAutoSubmitMagicLink ||
            shouldShowBecauseUnsponsored) &&
            hasFinished);
    const ariaProps = shouldShowContent ? {} : { 'aria-hidden': true };

    return (
        <>
            {!isReady && (
                <Box role="status">
                    <FullWidthLinearProgress />
                </Box>
            )}
            <Box
                sx={shouldShowContent ? undefined : styleHidden}
                {...ariaProps}
            >
                {children}
            </Box>
        </>
    );
};
